import React, { createContext, useContext, useMemo, useState } from "react";
import type {
  NavItemConfig,
  SubNavItemConfig,
} from "components/nav/AppSidebar";

interface SubNavContextValue {
  /** `url` of the nav item whose sub-nav is open, or "" when none is. */
  openSubNav: string;
  /** Opens a nav item's sub-nav, replacing any other. Pass "" to close. */
  showSubNav: (parentUrl: string) => void;
  /** Opens a nav item's sub-nav, or closes it when it is already the open one. */
  toggleSubNav: (parentUrl: string) => void;
  /** The current shell's nav config, so a page can resolve its own parent. */
  items: NavItemConfig[];
  subItems: SubNavItemConfig[];
}

const EMPTY_SUB_ITEMS: SubNavItemConfig[] = [];

const SubNavContext = createContext<SubNavContextValue>({
  openSubNav: "",
  showSubNav: () => {},
  toggleSubNav: () => {},
  items: [],
  subItems: [],
});

interface SubNavProviderProps {
  children: React.ReactNode;
  items: NavItemConfig[];
  subItems?: SubNavItemConfig[];
  /**
   * Brings the rail on screen. Supplied only while it is off screen (the
   * small-screen drawer), where a sub-nav opened from a page would otherwise
   * set state that nothing renders.
   */
  revealRail?: () => void;
}

/**
 * Holds which nav item's sub-nav is open, so pages can open one too.
 *
 * The state sits above the rail because the rail is not its only trigger: a
 * page's parent breadcrumb opens the sub-nav it belongs to, and a grouping
 * segment like `/admin/settings` is not a route it could navigate to instead.
 */
export const SubNavProvider: React.FC<SubNavProviderProps> = ({
  children,
  items,
  subItems = EMPTY_SUB_ITEMS,
  revealRail,
}) => {
  const [openSubNav, setOpenSubNav] = useState("");

  const value = useMemo<SubNavContextValue>(
    () => ({
      openSubNav,
      showSubNav: (parentUrl: string) => {
        if (parentUrl) revealRail?.();
        setOpenSubNav(parentUrl);
      },
      toggleSubNav: (parentUrl: string) =>
        setOpenSubNav((prev) => (prev === parentUrl ? "" : parentUrl)),
      items,
      subItems,
    }),
    [openSubNav, revealRail, items, subItems],
  );

  return (
    <SubNavContext.Provider value={value}>{children}</SubNavContext.Provider>
  );
};

/** Defaults to an empty, inert nav outside a shell, so pages render standalone. */
export const useSubNav = (): SubNavContextValue => useContext(SubNavContext);
